import { createUseStyles } from "react-jss";
import * as Mixins from "../../theme/Mixins";
import { Theme } from "../../types/Theme";

/**
 * `buttonPrimary`, `buttonSecondary`, `buttonNeutral`, and their Active variants are only
 * composed onto the DOM from `Button.tsx` when `Theme.UseModernTheme === true`
 * (with `outlineAccent` / `fillAccent`). Font-weight 500 here vs 700 on legacy `button` + `buttonActive`.
 */
const iconSizeFromSizeVariant = (size: unknown) => {
    if (typeof size === "number") return size;
    return size === "small" ? 16 : 24;
};

/** Theme-driven icon px only when modern nav chrome is on; otherwise legacy sizing. */
const resolveButtonIconPixelSize = (theme: Theme, size: unknown): number => {
    if (
        theme.UseModernTheme === true &&
        typeof theme.ButtonIconSize === "number" &&
        !Number.isNaN(theme.ButtonIconSize) &&
        theme.ButtonIconSize > 0
    ) {
        return theme.ButtonIconSize;
    }
    return iconSizeFromSizeVariant(size);
};

/**
 * Focus ring for modern nav variants only (reference: primary inset + white gap + primary halo).
 * Must not be applied to legacy `button` — undefined box-shadow on :focus would clear the inset border.
 */
const modernButtonFocusPseudoStyles = {
    "&:focus-visible": {
        boxShadow: ({ theme }: { theme: Theme }) => {
            if (theme.UseModernTheme !== true) {
                return undefined;
            }
            const gap = theme.WhiteColor ?? "#ffffff";
            const ring = theme.PrimaryColor;
            const inset = `0 0 0 ${theme.BorderWidth}px ${ring} inset`;
            return `${inset}, 0 0 0 2px ${gap}, 0 0 0 4px ${ring}`;
        },
    },
};

export default createUseStyles({
    button: {
        alignItems: "center",
        background: ({ theme }: { theme: Theme }) =>
            Mixins.alpha(theme.ButtonBackground, theme.ControlAlpha),
        borderRadius: ({ theme }: { theme: Theme }) => theme.BorderRadius,
        boxShadow: ({ theme }: { theme: Theme }) =>
            `0 0 0 ${theme.BorderWidth}px ${theme.ControlBorderColor} inset`,
        boxSizing: "border-box",
        color: ({ theme }: { theme: Theme }) =>
            Mixins.foregroundColor(theme, Mixins.buttonBackgroundColor(theme)),
        composes: "button",
        cursor: "pointer",
        display: "flex",
        fontWeight: 700,
        justifyContent: "center",
        minHeight: ({ theme }: { theme: Theme }) => theme.InputHeight + theme.BorderWidth * 2,
        outline: "none",
        padding: 6,
        transition: "background 200ms, box-shadow 200ms, color 200ms",
        userSelect: "none",
        fontFamily: ({ theme }: { theme: Theme }) => `"${theme.TextFont}", sans-serif`,
        "font-size": "16px",
        "line-height": "25.7778px",
    },
    buttonActive: {
        color: ({ theme }: { theme: Theme }) => Mixins.foregroundColor(theme, theme.PrimaryColor),
        background: ({ theme }: { theme: Theme }) => theme.PrimaryColor,
        boxShadow: ({ theme }: { theme: Theme }) =>
            `0 0 0 ${theme.BorderWidth}px ${Mixins.darken(theme.PrimaryColor, 0.5)} inset`,
    },
    /** Solid primary at rest (nav Next / Submit); fill colors from theme. */
    buttonPrimary: {
        alignItems: "center",
        background: ({ theme }: { theme: Theme }) => theme.PrimaryColor,
        borderRadius: ({ theme }: { theme: Theme }) => theme.BorderRadius,
        boxShadow: ({ theme }: { theme: Theme }) =>
            `0 0 0 ${theme.BorderWidth}px ${Mixins.darken(theme.PrimaryColor, 0.5)} inset`,
        boxSizing: "border-box",
        color: ({ theme }: { theme: Theme }) => Mixins.foregroundColor(theme, theme.PrimaryColor),
        composes: "button",
        cursor: "pointer",
        display: "flex",
        fontWeight: 500,
        justifyContent: "center",
        minHeight: ({ theme }: { theme: Theme }) => theme.InputHeight + theme.BorderWidth * 2,
        outline: "none",
        ...modernButtonFocusPseudoStyles,
        padding: 6,
        transition: "background 200ms, box-shadow 200ms, color 200ms",
        userSelect: "none",
        fontFamily: ({ theme }: { theme: Theme }) => `"${theme.TextFont}", sans-serif`,
        "font-size": "16px",
        "line-height": "25.7778px",
    },
    buttonPrimaryActive: {
        background: ({ theme }: { theme: Theme }) => Mixins.darken(theme.PrimaryColor, 0.2),
        boxShadow: ({ theme }: { theme: Theme }) =>
            `0 0 0 ${theme.BorderWidth}px ${Mixins.darken(Mixins.darken(theme.PrimaryColor, 0.2), 0.5)} inset`,
        color: ({ theme }: { theme: Theme }) =>
            Mixins.foregroundColor(theme, Mixins.darken(theme.PrimaryColor, 0.2)),
        fontWeight: 500,
    },
    buttonHidden: {
        composes: "button--hidden",
        visibility: "hidden",
    },
    buttonDisabled: {
        composes: "button--disabled",
        opacity: 0.7,
        pointerEvents: "none",
    },
    /** Secondary outline (nav Back): border/label from theme.PrimaryColor. */
    buttonSecondary: {
        alignItems: "center",
        background: ({ theme }: { theme: Theme }) => theme.InputBackground,
        borderRadius: ({ theme }: { theme: Theme }) => theme.BorderRadius,
        boxShadow: ({ theme }: { theme: Theme }) =>
            `0 0 0 ${theme.BorderWidth}px ${theme.PrimaryColor} inset`,
        boxSizing: "border-box",
        color: ({ theme }: { theme: Theme }) => theme.PrimaryColor,
        composes: "button",
        cursor: "pointer",
        display: "flex",
        fontWeight: 500,
        justifyContent: "center",
        minHeight: ({ theme }: { theme: Theme }) => theme.InputHeight + theme.BorderWidth * 2,
        outline: "none",
        ...modernButtonFocusPseudoStyles,
        padding: 6,
        transition: "background 200ms, box-shadow 200ms, color 200ms",
        userSelect: "none",
        fontFamily: ({ theme }: { theme: Theme }) => `"${theme.TextFont}", sans-serif`,
        "font-size": "16px",
        "line-height": "25.7778px",
    },
    buttonSecondaryActive: {
        /** Match Quit (`buttonNeutralActive`): neutral gray tint on hover/focus, not accent-tinted fill. */
        background: ({ theme }: { theme: Theme }) => Mixins.alpha(theme.NeutralDarkColor, 0.08),
        color: ({ theme }: { theme: Theme }) => theme.PrimaryColor,
        fontWeight: 500,
    },
    /** Neutral outline (nav Quit): border/label from theme.BlackColor. */
    buttonNeutral: {
        alignItems: "center",
        background: ({ theme }: { theme: Theme }) => theme.InputBackground,
        borderRadius: ({ theme }: { theme: Theme }) => theme.BorderRadius,
        boxShadow: ({ theme }: { theme: Theme }) =>
            `0 0 0 ${theme.BorderWidth}px ${theme.BlackColor} inset`,
        boxSizing: "border-box",
        color: ({ theme }: { theme: Theme }) => theme.BlackColor,
        composes: "button",
        cursor: "pointer",
        display: "flex",
        fontWeight: 500,
        justifyContent: "center",
        minHeight: ({ theme }: { theme: Theme }) => theme.InputHeight + theme.BorderWidth * 2,
        outline: "none",
        ...modernButtonFocusPseudoStyles,
        padding: 6,
        transition: "background 200ms, box-shadow 200ms, color 200ms",
        userSelect: "none",
        fontFamily: ({ theme }: { theme: Theme }) => `"${theme.TextFont}", sans-serif`,
        "font-size": "16px",
        "line-height": "25.7778px",
    },
    buttonNeutralActive: {
        background: ({ theme }: { theme: Theme }) => Mixins.alpha(theme.NeutralDarkColor, 0.08),
        color: ({ theme }: { theme: Theme }) => theme.BlackColor,
        fontWeight: 500,
    },
    buttonSmall: {
        composes: "button--small",
        fontSize: 13,
        fontWeight: "initial",
        margin: [[0, 6]],
        padding: [[6, 12]],
    },
    buttonLarge: {
        composes: "button--large",
        flexBasis: 180,
        margin: [[12, 6]],
        padding: [[12, 12]],
    },
    buttonFull: {
        composes: "button--full",
        margin: [[27, 0]],
        padding: [[12, 12]],
    },
    buttonFlex: {
        composes: "button--flex",
        flex: 1,
        margin: [[12, 6]],
        padding: [[12, 12]],
    },
    buttonTextContainer: {
        composes: "button__text",
    },
    buttonBorderNone: {
        border: "none",
    },
    buttonTextAnimated: {
        transition: "transform 200ms ease-in-out",
    },
    buttonTextAnimatedHover: {
        transform: ({ iconPosition }: { iconPosition?: string }) =>
            `translate(${iconPosition === "start" ? "13px" : "-13px"})`,
    },
    buttonIconContainer: {
        composes: "button__container",
        height: ({ theme, size }: { theme: Theme; size?: any }) =>
            `${resolveButtonIconPixelSize(theme, size)}px`,
        lineHeight: ({ theme, size }: { theme: Theme; size?: any }) =>
            `${resolveButtonIconPixelSize(theme, size)}px`,
        marginLeft: ({
            iconPosition,
            iconOnHover,
            textAndIcon,
        }: {
            iconPosition?: string;
            iconOnHover?: boolean;
            textAndIcon?: boolean;
        }) => (iconPosition === "end" && textAndIcon && !iconOnHover ? 10 : 0),
        marginRight: ({
            iconPosition,
            iconOnHover,
            textAndIcon,
        }: {
            iconPosition?: string;
            iconOnHover?: boolean;
            textAndIcon?: boolean;
        }) => (iconPosition === "start" && textAndIcon && !iconOnHover ? 10 : 0),
        pointerEvents: "none",
        position: "relative",
        verticalAlign: "middle",
    },
    buttonIcon: {
        composes: "button__icon",
        fontSize: ({ theme, size }: { theme: Theme; size?: any }) =>
            resolveButtonIconPixelSize(theme, size),
        width: ({ theme, size }: { theme: Theme; size?: any }) =>
            resolveButtonIconPixelSize(theme, size),
        left: ({
            iconPosition,
            iconOnHover,
            textAndIcon,
        }: {
            iconPosition?: string;
            iconOnHover?: boolean;
            textAndIcon?: boolean;
        }) => (iconPosition === "start" && textAndIcon && !iconOnHover ? 0 : -12),
        position: ({
            iconOnHover,
            textAndIcon,
        }: {
            iconOnHover?: boolean;
            textAndIcon?: boolean;
        }) => (textAndIcon && iconOnHover ? "absolute" : "initial"),
        right: ({
            iconPosition,
            iconOnHover,
            textAndIcon,
        }: {
            iconPosition?: string;
            iconOnHover?: boolean;
            textAndIcon?: boolean;
        }) => (iconPosition === "end" && textAndIcon && !iconOnHover ? 0 : -12),
        textAlign: "center",
    },
    buttonIconAnimated: {
        opacity: 0,
        transform: ({ iconPosition }: { iconPosition?: string }) =>
            `translate(${iconPosition === "start" ? "5px" : "-5px"})`,
        transition: "transform 200ms ease-in-out, opacity 200ms ease-in-out",
    },
    buttonIconAnimatedHover: {
        opacity: 1,
        transform: ({ iconPosition }: { iconPosition?: string }) =>
            `translate(${iconPosition === "start" ? "-8px" : "8px"})`,
    },
    buttonIconDisabled: {
        composes: "button__icon--disabled",
    },
    pointer: {
        cursor: "pointer",
    },
} as any);
